<script lang="ts">
	// The owner's overview: the currency and time zone the restaurant runs
	// on, then one card per shop with today's money. Cash, card and what is
	// still due are the figures an owner checks first; the counts under them
	// are for when something looks off. Each card opens that shop's orders.

	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Chip from '../components/Chip.svelte';
	import Loading from '../components/Loading.svelte';
	import { message, type Language, type MessageKey } from '../i18n/messages';
	import { formatMinor } from '../format/money';
	import { navigate } from '../router.svelte';
	import type { Account } from '../owner/session.svelte';
	import { loadDays, ownerShops, ownerDays, setOrdersShop } from '../owner/data.svelte';

	interface Props {
		language: Language;
		account: Account;
		shopId?: string;
		onOrders?: (id: string) => void;
		onShop?: (id: string) => void;
	}

	let { language, account, shopId = '', onOrders, onShop }: Props = $props();

	const shops = $derived(ownerShops());
	const days = $derived(ownerDays());
	const minorDigits = $derived(account.settings.minorDigits);

	// Today's figures, read once the shops are known.
	$effect(() => {
		if (shops.loaded) void loadDays();
	});

	// The currency's own name, in the page's language, from the browser.
	const currencyName = $derived.by(() => {
		try {
			return (
				new Intl.DisplayNames([language], { type: 'currency' }).of(account.settings.currencyCode) ??
				''
			);
		} catch {
			return '';
		}
	});

	function figures(id: string): { key: MessageKey; value: string }[] {
		const summary = days.days[id]?.summary;
		return [
			{ key: 'ordersCash', value: formatMinor(summary?.cashMinor ?? 0, minorDigits) },
			{ key: 'ordersCard', value: formatMinor(summary?.cardMinor ?? 0, minorDigits) },
			{ key: 'ordersDue', value: formatMinor(summary?.dueMinor ?? 0, minorDigits) },
			{ key: 'ordersPaid', value: String(summary?.paidCount ?? 0) },
		];
	}

	function counts(id: string): { key: MessageKey; value: number }[] {
		const summary = days.days[id]?.summary;
		return [
			{ key: 'ordersOpen', value: summary?.openCount ?? 0 },
			{ key: 'ordersVoid', value: summary?.voidCount ?? 0 },
			{ key: 'ordersNoSale', value: summary?.noSaleCount ?? 0 },
		];
	}
</script>

<div class="home" data-testid="panel-home">
	<div class="facts">
		<Chip
			text="{account.settings.currencyCode}{currencyName ? ` · ${currencyName}` : ''}"
			size="md"
			testId="fact-currency"
		/>
		<Chip text={account.settings.timezone} size="md" testId="fact-timezone" />
	</div>

	{#if !shops.loaded || !days.loaded}
		<div class="wait">
			<Loading
				size="lg"
				label={message(language, 'loading')}
				caption={message(language, 'loading')}
			/>
		</div>
	{:else}
		<div class="shops" data-testid="fact-shops">
			{#each shops.value.filter((shop) => !shopId || shop.id === shopId) as shop (shop.id)}
				<Card title={shop.name} note={message(language, 'homeToday')} testId="home-shop-{shop.id}">
					<div class="body">
						<div class="figures">
							{#each figures(shop.id) as figure (figure.key)}
								<div class="figure">
									<span class="label">{message(language, figure.key)}</span>
									<span class="value" dir="ltr">{figure.value}</span>
								</div>
							{/each}
						</div>
						<p class="counts">
							{#each counts(shop.id) as count (count.key)}
								<span class="count"
									>{message(language, count.key)}
									<bdi class="n" dir="ltr">{count.value}</bdi></span
								>
							{/each}
						</p>
						<div>
							{#if !shopId && onShop}
								<Button
									testId="home-open-shop-{shop.id}"
									label={message(language, 'ownerOpenShop')}
									tone="secondary"
									size="sm"
									onPress={() => onShop?.(shop.id)}
								/>
							{/if}
							<Button
								testId="home-orders-{shop.id}"
								label={message(language, 'homeShopOrders')}
								tone="text"
								size="sm"
								onPress={() => {
									setOrdersShop(shop.id);
									if (onOrders) onOrders(shop.id);
									else navigate('/owner/orders');
								}}
							/>
						</div>
					</div>
				</Card>
			{/each}
		</div>
	{/if}
</div>

<style>
	.home {
		display: flex;
		flex-direction: column;
		gap: var(--space-9);
	}

	.facts {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-3);
	}

	.wait {
		display: grid;
		place-items: center;
		min-block-size: 240px;
	}

	.shops {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
		gap: var(--grid-gap);
	}

	.body {
		padding: var(--card-pad);
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.figures {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: var(--space-7);
	}

	.figure {
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.label {
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.value {
		font-family: var(--font-mono);
		font-size: var(--figure-sm);
		font-weight: 700;
		line-height: var(--leading-tight);
		text-align: start;
	}

	.counts {
		margin: 0;
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-2) var(--space-7);
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.count {
		white-space: nowrap;
	}

	.n {
		font-family: var(--font-mono);
		font-weight: 700;
		color: var(--text-strong);
	}
</style>
